import {
  FilesetResolver,
  PoseLandmarker,
  type PoseLandmarkerResult,
} from '@mediapipe/tasks-vision';
import type { Landmark, PoseFrame } from '@tbc/core';

/**
 * MediaPipe Tasks Vision WASM bundle. Pinned to a specific CDN version so the
 * build is reproducible. Bumping requires testing on Chrome Android.
 */
const WASM_BASE_URL =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.17/wasm';

/**
 * Pose Landmarker (full) model — 9MB, 3D landmarks. Lite is ~3MB but trades
 * accuracy on partial visibility (which we will see often during swings).
 */
const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/1/pose_landmarker_full.task';

export interface PoseRunnerOptions {
  /** Override CDN for offline / pinned scenarios. */
  readonly wasmBaseUrl?: string;
  readonly modelUrl?: string;
  /** Use 'IMAGE' for single frames or 'VIDEO' for streaming with timestamps. */
  readonly runningMode?: 'IMAGE' | 'VIDEO';
  /** Hint for the inference backend; falls back gracefully on unsupported devices. */
  readonly delegate?: 'GPU' | 'CPU';
  /**
   * Maximum number of poses to detect per frame. Default 1 (single-player
   * recording). Use 2 for rallies with both players in view.
   */
  readonly numPoses?: 1 | 2;
}

/**
 * Thin wrapper that exposes a Promise-friendly factory and a per-frame
 * `detectFrame(...)` returning normalized `PoseFrame` data, decoupled from
 * MediaPipe's raw types so the rest of the app depends only on `@tbc/core`.
 */
export class PoseRunner {
  private constructor(private readonly inner: PoseLandmarker) {}

  static async create(opts: PoseRunnerOptions = {}): Promise<PoseRunner> {
    const wasmBase = opts.wasmBaseUrl ?? WASM_BASE_URL;
    const modelUrl = opts.modelUrl ?? MODEL_URL;
    const fileset = await FilesetResolver.forVisionTasks(wasmBase);
    const landmarker = await PoseLandmarker.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: modelUrl,
        delegate: opts.delegate ?? 'GPU',
      },
      runningMode: opts.runningMode ?? 'VIDEO',
      numPoses: opts.numPoses ?? 1,
      minPoseDetectionConfidence: 0.5,
      minPosePresenceConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });
    return new PoseRunner(landmarker);
  }

  /**
   * Run pose detection on a single frame and return up to `numPoses` poses.
   * The `timestampMs` must increase monotonically when
   * `runningMode === 'VIDEO'`. Returns an empty array when nothing was
   * detected.
   */
  detectFrameMulti(
    source: HTMLVideoElement | HTMLCanvasElement | ImageBitmap,
    timestampMs: number,
  ): PoseFrame[] {
    const result: PoseLandmarkerResult = this.inner.detectForVideo(source, timestampMs);
    return result.landmarks.map((pose) =>
      pose.map<Landmark>((l) => ({
        x: l.x,
        y: l.y,
        z: l.z,
        visibility: l.visibility ?? 0,
      })),
    );
  }

  /**
   * Convenience wrapper returning only the first detected pose, for
   * backward-compatible single-player use.
   */
  detectFrame(
    source: HTMLVideoElement | HTMLCanvasElement | ImageBitmap,
    timestampMs: number,
  ): PoseFrame | null {
    const poses = this.detectFrameMulti(source, timestampMs);
    return poses[0] ?? null;
  }

  close(): void {
    this.inner.close();
  }
}
